/**
 * Layer B: the web chat client contract.
 *
 * What a chat client hands to application code. A flat discriminated union on a
 * `type` field, camelCase. `packages/stream-core` consumes this and never sees
 * layer A. The published name for this union is `MatildaChatStreamEvent`.
 *
 * There are three layers in this product and only two of them are modelled
 * here. See docs/PROTOCOL.md for the table and for why layer C is excluded.
 *
 * Provenance. The union is modelled on the declarations published by
 * `matilda-client-sdk`, version 0.2.0, held in the local reference workspace
 * outside this repository. Every name and shape in this file was checked
 * against that declaration file directly. docs/PROTOCOL.md records the file
 * and line range per row, and DECISIONS.md entry 3 records why these rows
 * once rested on a citation and how that gap was closed.
 *
 * `ChatErrorCode`, `UsageEvent`, `ActiveStreamLookup`, and `FeedbackReason`
 * were checked directly and their line ranges are in docs/PROTOCOL.md.
 */

/**
 * An open union. The six named values are published. The `string & {}` member
 * keeps it open so an unrecognised status from a newer server widens the type
 * instead of breaking the build.
 * // observed
 */
export type ResponseStatus =
  | 'thinking'
  | 'streaming'
  | 'queued'
  | 'idle'
  | 'done'
  | 'error'
  // The intersection is what keeps the union open to an unrecognised status
  // from a newer server while preserving completion on the six named members.
  | (string & {});

/**
 * All ten codes, as declared.
 * // observed
 *
 * Read the note on REFERENCE_RUNTIME_ERROR_CODES before assuming a wire frame
 * can carry every one of these.
 */
export const CHAT_ERROR_CODES = [
  'internal_error',
  'upstream_unavailable',
  'rate_limited',
  'content_blocked',
  'stream_aborted',
  'deadline_exceeded',
  'context_too_large',
  'stalled',
  'stream_expired',
  'unknown',
] as const;

// observed
export type ChatErrorCode = (typeof CHAT_ERROR_CODES)[number];

const CHAT_ERROR_CODE_SET: ReadonlySet<string> = new Set<string>(CHAT_ERROR_CODES);

/** True when the string is one of the ten declared codes. */
export function isChatErrorCode(code: string): code is ChatErrorCode {
  return CHAT_ERROR_CODE_SET.has(code);
}

/**
 * The nine codes the reference parser will actually accept off the wire.
 * // observed
 *
 * The declaration names ten. The runtime validator set omits `stream_expired`,
 * so a wire error frame carrying that code is downgraded to `unknown` by the
 * reference client. Our server emits `stream_expired` for the ring buffer
 * expiry rule, and our adapter accepts it, which makes us a deliberate superset
 * of reference behaviour rather than an accidental one. This constant exists so
 * a test can assert we know the difference. See docs/DECISIONS.md entry 4.
 */
export const REFERENCE_RUNTIME_ERROR_CODES: readonly ChatErrorCode[] = CHAT_ERROR_CODES.filter(
  (code) => code !== 'stream_expired',
);

/**
 * snake_case is preserved because that is how the shape is published, and it is
 * the one place layer B does not translate case. Do not "fix" it. There is a
 * test pinning the asymmetry, because it looks like a bug and someone will try.
 * // observed
 */
export interface UsageEvent {
  output_tokens: number;
  context_pct?: number;
  context_messages_trimmed?: number;
  context_budget_tokens?: number;
}

// observed
export interface ActiveStreamLookup {
  stream_id: string | null;
  status: 'active' | 'done' | 'error' | null;
}

// observed
export type FeedbackReason = 'harmful' | 'inaccurate' | 'off_topic' | 'privacy' | 'other';

/**
 * A tool invocation as the client accumulates it across the three tool events.
 * // observed
 *
 * The published shape carries no timestamps. The two timing fields at the end
 * are ours, marked in place: the reasoning panel reports elapsed time per tool
 * call, so the client records when it saw the start and end frames. Both are
 * optional so every published value remains assignable. See DECISIONS.md
 * entry 15 for the shape correction this interface went through.
 */
export interface ToolCall {
  tool: string;
  status: 'running' | 'success' | 'error';
  id?: string;
  input?: string;
  progress?: string;
  steps?: string[];
  output?: string;
  args?: Record<string, unknown> | string;
  // extension
  startedAt?: number;
  // extension
  endedAt?: number;
}

/**
 * The assembled client side reasoning shape.
 * // observed
 *
 * The wire has no reasoning delta event. It has `thinking`, a status signal,
 * and `generation_status`, a phase string, and this shape is what a client
 * assembles from those two. Both facts are true at once: the shape is
 * published, and nothing on the wire fills it in directly.
 */
export interface MessageReasoning {
  content?: string;
  status?: 'thinking' | 'done';
  thinkingContent?: string;
  toolCalls?: ToolCall[];
  totalElapsedMs?: number;
}

/**
 * The fourteen published members, plus one extension.
 * // observed
 *
 * Note two members with no wire event of their own. `response.cursor` is
 * synthesised by the state machine from the `id:` line once the payload has
 * been applied. `response.status` is reached from two directions: the wire
 * `thinking` event, and the wire `status` event when its state is not `queued`.
 */
export type MatildaChatStreamEvent =
  | { type: 'response.created'; streamId: string }
  | { type: 'response.output_text.delta'; delta: string }
  | { type: 'response.output_text.replace'; content?: string; categories?: string[] }
  | { type: 'response.queued'; state: string; position: number; estimatedWaitSeconds: number }
  | {
      type: 'response.tool_call.started';
      tool: string;
      inputOrArgs?: string | Record<string, unknown>;
      output?: string;
    }
  | { type: 'response.tool_call.progress'; tool: string; message: string }
  | {
      type: 'response.tool_call.completed';
      tool: string;
      status: 'success' | 'error';
      input?: string;
      output?: string;
    }
  | { type: 'response.status'; status: ResponseStatus }
  | { type: 'response.generation_status'; phase: string }
  | { type: 'response.cursor'; lastEventId: string }
  | { type: 'response.usage'; usage: UsageEvent }
  | { type: 'response.truncated'; reason: string }
  | { type: 'response.completed' }
  | { type: 'response.error'; code: ChatErrorCode; message: string }
  /**
   * // extension
   *
   * The wire carries a `client_tool_call` event and the published union has no
   * member for it, because client side tool execution belongs to the agent
   * composition layer rather than to a web chat client. The server in this
   * project emits the wire event, so the adapter needs somewhere to put it.
   * Dropping it would lose a documented wire event, which is worse than
   * carrying one clearly marked extension.
   */
  | {
      type: 'response.client_tool_call';
      name: string;
      args: Record<string, unknown>;
      callId?: string;
    };

/** The `type` discriminant of every layer B event. */
export type MatildaChatStreamEventType = MatildaChatStreamEvent['type'];

/**
 * The fourteen published member names, for the test that asserts the adapter
 * covers the union and invents nothing.
 * // observed
 */
export const PUBLISHED_EVENT_TYPES = [
  'response.created',
  'response.output_text.delta',
  'response.output_text.replace',
  'response.queued',
  'response.tool_call.started',
  'response.tool_call.progress',
  'response.tool_call.completed',
  'response.status',
  'response.generation_status',
  'response.cursor',
  'response.usage',
  'response.truncated',
  'response.completed',
  'response.error',
] as const;
